import OCButton from '@cx-web/components/primitives/button';
import Checkbox from '@cx-web/components/primitives/Checkbox';
import TextInput from '@cx-web/components/primitives/TextInput';
import { useCxRuntime } from '@cx-web/feature-cx-runtime';
import classnames from 'classnames';

import { useOnboardingActions } from './store/actions';
import { type OnboardingFetcher, type OnboardingProps } from './store/types';
import styles from './Onboarding.module.css';

// NGF-6334: the single shared Onboarding view, consumed by both apps/atlas (RR7) and apps/atlas-astro
// (island). All framework seams are read through useCxRuntime() — labels via `t` (bare keys; the
// adapter handles the RESPONSIVE_ prefix), static assets via `resourcesUrl` — so this source is
// byte-identical across both apps. The `fetcher` is injected per app (RR7 useFetcher / atlas-astro
// native-fetch hook).
export const Onboarding = ({ fetcher, ...props }: OnboardingProps & { fetcher: OnboardingFetcher }) => {
  const { hasMarginTop, variant } = props;

  const runtime = useCxRuntime();
  const t = runtime.useLabel();
  const { resourcesUrl } = runtime.useConfig();

  const {
    agreeTerms,
    desktopAndPrimary,
    disabled,
    errorOnBlur,
    handleInputBlur,
    handleInputChange,
    handleSubmit,
    input,
    inputIsValid,
    mobileAndSecondary,
    shouldRenderAsMyOCLoginCTA,
    shouldRenderAsSecondary,
    submitted,
    toggleTerms,
  } = useOnboardingActions({ variant, view: props.view }, fetcher);

  // Renderers

  const renderBgImg = () => {
    const backgroundImage = shouldRenderAsSecondary ? 'nba' : 'nfl';
    const backgroundImageUrl = `${resourcesUrl}/assets/img/onboarding/signup-${backgroundImage}.webp`;

    return (
      <picture>
        <source srcSet={backgroundImageUrl} type="image/webp" />
        <img
          alt="onboarding-background"
          className={styles.bgImg}
          fetchPriority="high"
          loading="eager"
          src={backgroundImageUrl}
        />
      </picture>
    );
  };

  const renderTitle = () => {
    return (
      <div className={styles.onboardingTitle}>
        <h2>{t('SIGN_UP_WITH_ODDSCHECKER')}</h2>
        <h2 className={styles.highlight}>{t('TO_BEAT_THE_ODDS')}</h2>
      </div>
    );
  };

  const renderDescription = () => {
    return <div className={styles.description}>{t('SUBSCRIBE_TO_EMAIL')}</div>;
  };

  const renderTextInput = () => {
    return (
      <TextInput
        data-testid="onboarding-email-input"
        hasError={errorOnBlur}
        inputClassName={classnames(styles.input, errorOnBlur ? styles.error : '')}
        isValid={inputIsValid}
        name="email"
        onBlur={handleInputBlur}
        onChange={({ target }) => handleInputChange(target.value)}
        placeholder={t('YOUR_EMAIL_ADDRESS')}
        shouldAutocomplete={false}
        type="email"
        value={input.value}
        wrapperClassName={styles.inputContainer}
      />
    );
  };

  const renderErrorBanner = () => {
    if (!errorOnBlur) return null;

    return (
      <div className={styles.errorBanner} data-testid="error-banner">
        <div className={styles.errorTitle}>{t('INVALID_EMAIL')}</div>
        <div className={styles.errorMessage}>{t('EMAIL_ADDRESS_FORMAT')}</div>
      </div>
    );
  };

  const renderCTA = () => {
    // TODO: add SIGNING_UP label 'Signing up...'
    const ctaText =
      fetcher.state === 'loading' || fetcher.state === 'submitting'
        ? t('SIGNING_UP') || 'Signing up...'
        : shouldRenderAsMyOCLoginCTA || !submitted
          ? t('SIGN_ME_UP')
          : t('SIGNED_UP');

    return (
      <div className={styles.ctaWrapper}>
        <OCButton
          className={styles.ctaBtn}
          disabled={disabled}
          onClick={handleSubmit}
          testId="onboarding-submit"
          type="secondary"
        >
          {ctaText}
        </OCButton>
      </div>
    );
  };

  const renderLegalCheckbox = () => {
    if (shouldRenderAsMyOCLoginCTA) return null;

    const termsAndPrivacyLabel = t('YES_TO_TERMS').replace(/%1/gi, t('TERMS_URL')).replace(/%2/gi, t('PRIVACY_URL'));

    return (
      <Checkbox checked={agreeTerms} className={styles.terms} name="terms" onClick={toggleTerms}>
        <span
          className={styles.terms}
          dangerouslySetInnerHTML={{
            __html: termsAndPrivacyLabel,
          }}
          data-testid="onboarding-terms"
        />
      </Checkbox>
    );
  };

  return (
    <div
      className={classnames(
        styles.wrapper,
        shouldRenderAsSecondary ? styles.secondary : styles.primary,
        shouldRenderAsMyOCLoginCTA ? styles.isSignupCta : '',
        hasMarginTop ? styles.marginTop : '',
      )}
      data-testid="onboarding"
    >
      {renderBgImg()}
      <div className={styles.bgColor} />
      <div className={styles.contentWrapper}>
        {renderTitle()}
        <div className={styles.formContainer}>
          {renderDescription()}
          <div className={styles.inputWrapper}>
            {renderTextInput()}
            {mobileAndSecondary ? renderErrorBanner() : null}
            {renderCTA()}
          </div>
          {desktopAndPrimary ? renderErrorBanner() : null}
          {renderLegalCheckbox()}
        </div>
      </div>
    </div>
  );
};
